<template>
  <v-card class="py-2 mx-auto" :max-width="MAX_CONTENT_WIDTH">
    <h1 class="page-title"> Explore {{ userFriendlyMediumWord(this.$route.params.medium, true) }} </h1>
    <v-divider></v-divider>
    <div class="d-flex justify-end">
      <div class="filter-wrapper"
        ><select-dense
          v-model="sortBy"
          :items="sortOptions"
          @change="handleFilterChanged"
          item-text="userFriendly"
          item-value="value"
          :clearable="false"
        ></select-dense>
      </div>
    </div>
    <v-divider></v-divider>
    <infinite-feed :enabled="!loadingMoreResults && hasMoreResults" @load-more="handleLoadMore">
      <v-list class="py-0" id="work-list">
        <template v-for="item in works">
          <v-list-item :key="works.indexOf(item)" class="py-2 pl-5 d-flex align-start">
            <v-img
              :src="item.posterSource"
              class="reviewPoster rounded"
              :height="posterWidth / getAspectRatio(item.medium)"
              :width="posterWidth"
              :max-width="posterWidth"
              @click="routeToWorkPage(item.medium, item.id)"
            >
              <template v-slot:placeholder>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-progress-circular indeterminate></v-progress-circular>
                </v-row>
              </template>
            </v-img>
            <div class="work-info">
              <p class="work-title">
                <router-link :to="{ name: 'work', params: { medium: item.medium, workId: item.id } }">
                  <strong> {{ item.title }}</strong>
                </router-link>
              </p>
              <p class="details">
                {{ workSubtext(item) }}
              </p>
              <p class="rating" v-if="item.rating">
                {{ item.rating % 1 === 0 ? parseInt(item.rating) : item.rating.toFixed(1) }}/10
              </p>
              <p class="audience-size">
                <v-icon size="16" class="audience-size-icon">mdi-account-group</v-icon>
                {{ item.audienceSize }}
              </p>
            </div>
            <work-interaction-utility-button
              v-if="isLoggedIn"
              :work-id="item.id"
              :medium="item.medium"
              :user-work-relationships="item.userInfo"
              :icon-size="18"
              no-padding
              @change-engaged="handleChangeEngaged($event, item)"
              @change-saved-for-later="handleChangeSavedForLater($event, item)"
            ></work-interaction-utility-button>
          </v-list-item>
          <v-divider :key="item.id + 'divider'" />
        </template>
      </v-list>
    </infinite-feed>
  </v-card>
</template>

<script>
import { mapMutations } from "vuex";

// Components
import SelectDense from "../components/select-dense.vue";
import WorkInteractionUtilityButton from "../components/work-interaction-utility-button.vue";
import InfiniteFeed from "../components/infinite-feed.vue";
// Mixins
import getTopWorks from "../mixins/getTopWorks";
import SessionTools from "../mixins/sessionTools";
// Util
import { MAX_CONTENT_WIDTH } from "../util/constants";
import { userFriendlyMediumWord } from "../util/mediumHelpers";
import getAspectRatio from "../util/getAspectRatio";
import { formatDate } from "../util/dateHelpers";

export default {
  name: "explore-page",
  components: { SelectDense, WorkInteractionUtilityButton, InfiniteFeed },
  mixins: [getTopWorks, SessionTools],

  data: () => ({
    MAX_CONTENT_WIDTH,
    ITEMS_PER_LOAD: 100,

    works: [],
    hasMoreResults: true,
    loadingMoreResults: false,
    headers: [
      { text: "Title", value: "title" },
      { text: "Telemonial Average", value: "average" },
    ],
    sortBy: "audienceSize",
    sortOptions: [
      {
        userFriendly: "Audience Size",
        value: "audienceSize",
      },
      {
        userFriendly: "Rating",
        value: "rating",
      },
    ],
  }),

  computed: {
    aspect() {
      let medium = this.$route.params.medium;
      return [this.$mediums.ALBUM, this.$mediums.PODCAST].includes(medium) ? 1 : 2 / 3;
    },
    posterWidth() {
      if (this.$vuetify.breakpoint.mdAndUp) {
        return 100;
      } else {
        return 64;
      }
    },
  },

  watch: {
    $route() {
      this.resetWorks();
    },
  },

  mounted: async function () {
    if (
      this.$store.state.explorePage.results.length === 0 ||
      this.$store.state.explorePage.medium !== this.$route.params.medium ||
      this.$store.state.username !== this.$store.state.explorePage.authenticatedUser // Needed to update work-relations
    ) {
      await this.loadMoreWorks();
    } else {
      await this.loadPageFromStore();
    }
  },

  beforeRouteLeave(to, from, next) {
    this.saveExplorePage({
      medium: this.$route.params.medium,
      results: this.works,
      scrollY: document.documentElement.scrollTop,
      endOfResults: !this.hasMoreResults,
      sortBy: this.sortBy,
      authenticatedUser: this.$store.state.username,
    });

    next();
  },

  methods: {
    ...mapMutations(["saveExplorePage"]),
    userFriendlyMediumWord,
    getAspectRatio,
    formatDate,

    loadPageFromStore: async function () {
      const explorePageState = this.$store.state.explorePage;
      this.works = explorePageState.results;
      this.hasMoreResults = !explorePageState.endOfResults;
      this.sortBy = explorePageState.sortBy;
      await this.$vuetify.goTo(explorePageState.scrollY, {
        duration: 0,
      });
    },

    loadMoreWorks: async function () {
      if (this.hasMoreResults && !this.loadingMoreResults) {
        this.loadingMoreResults = true;
        const newWorks = await this.getTopWorksV1(
          this.$route.params.medium,
          this.ITEMS_PER_LOAD,
          this.sortBy,
          this.works.length,
        );
        this.works = this.works.concat(newWorks);
        if (newWorks.length < this.ITEMS_PER_LOAD) {
          this.hasMoreResults = false;
        }
        this.loadingMoreResults = false;
      }
    },

    resetWorks: async function () {
      this.works = [];
      this.hasMoreResults = true;
      this.loadMoreWorks();
    },

    workSubtext(work) {
      let text = "";
      if (work.creators && work.creators.length > 0) {
        text += work.creators.map((creator) => creator.name).join(",");
      }
      if (work.creators && work.creators.length > 0 && work.releaseDate) {
        text += " • ";
      }
      if (work.releaseDate) {
        text += this.formatDate(work.releaseDate);
      }
      return text;
    },

    routeToWorkPage: async function (medium, id) {
      await this.$router.push({
        name: "work",
        params: {
          medium,
          workId: id,
        },
      });
    },

    routeToRatePage: async function (medium, id) {
      await this.$router.push({
        path: "/rate/",
        query: {
          medium,
          workID: id,
        },
      });
    },

    handleFilterChanged: async function () {
      this.resetWorks();
    },

    handleLoadMore: async function () {
      this.loadMoreWorks();
    },

    handleChangeEngaged(event, work) {
      console.log("Handling change engaged");
      console.log(event);
      work.userInfo.hasEngaged = event;
    },

    handleChangeSavedForLater(event, work) {
      work.userInfo.savedForLater = event;
      // TODO: Update saved-for-later state of the work
    },
  },
};
</script>

<style scoped>
.filter-wrapper {
  width: 180px;
}

.reviewPoster {
  cursor: pointer;
  justify-content: center;
  margin-right: 6px;
}

.work-info {
  height: 100%;
  text-align: start;
  flex-grow: 1;
}

.work-info p {
  margin-bottom: 0;
}

.work-title {
  line-height: 1;
}

.work-title a {
  color: var(--v-text-base);
}
.work-title a:hover {
  text-decoration: underline;
}

.work-info .details {
  font-size: 12px;
  color: var(--v-text-lighten1);
}

.rating {
  font-weight: bold;
  font-size: 16px;
  line-height: 1;
}

.audience-size {
  color: var(--v-text-lighten1);
  font-size: 14px;
  line-height: 1;
}
.audience-size .audience-size-icon {
  transform: translate(0, -2px);
}

@media (min-width: 960px) {
  .work-info p {
    margin-bottom: 1px;
  }
  .work-title {
    font-size: 18px;
  }
  .rating {
    font-size: 18px;
  }
  .work-info .details {
    font-size: 16px;
  }
  .audience-size {
    font-size: 16px;
  }
}
</style>
